import type { MetricDefinition } from '../types/weeklyMetrics';
import { fetchTimeToFirstPlan, fetchTimeToSeventhPlan } from '../services/planTimingData';
import { fetchDeedFilings } from '../services/deedFilingsData';
import { fetchDemoEvents } from '../services/demoEventsData';

export const metricDefinitions: MetricDefinition[] = [
  // ── MONTHLY ACTIVE USERS ───────────────────────────────────
  {
    key: 'mau',
    name: 'Monthly Active Accounts',
    section: 'ACTIVE ACCOUNTS',
    description: 'Distinct firms that submitted at least one plan in the period',
    query:
      "SELECT Encore_Firm__c, Submitted_Date__c FROM Opportunity WHERE IsWon = true AND RecordTypeId = '012Vs000004gYUQIA2' AND Submitted_Date__c >= {startDateTime}",
    dateField: 'Submitted_Date__c',
    aggregation: 'countDistinct',
    distinctField: 'Encore_Firm__c',
    format: 'number',
    isPrimary: true,
    segmentObject: 'Opportunity',
  },

  // ── DEMOS ──────────────────────────────────────────────────
  {
    key: 'demos',
    name: 'Demos Booked',
    section: 'DEMOS BOOKED',
    description:
      'Distinct demos scheduled that were not canceled (Calendly + manually booked, calendar-sync duplicates collapsed)',
    query: '',
    customFetcher: fetchDemoEvents,
    dateField: 'ActivityDate',
    aggregation: 'countDistinct',
    distinctField: 'dedupKey',
    format: 'number',
    isPrimary: true,
    // No segmentObject — demos are not tied to account segment
  },

  // ── ACCOUNTS ──────────────────────────────────────────────
  {
    key: 'new-accounts',
    name: 'New Accounts',
    section: 'ACCOUNTS',
    description: 'Distinct firms created in the period (from Encore_Firm__c)',
    query:
      'SELECT EEP_Firm_ID__c, EEP_Firm_Created_Date__c FROM Encore_Firm__c WHERE EEP_Firm_ID__c != null AND EEP_Firm_Created_Date__c >= {startDate}',
    dateField: 'EEP_Firm_Created_Date__c',
    aggregation: 'countDistinct',
    distinctField: 'EEP_Firm_ID__c',
    format: 'number',
    isPrimary: true,
    segmentObject: 'Encore_Firm__c',
  },
  {
    key: 'churn',
    name: 'Churn',
    section: 'CHURN',
    description: 'Firms with 7+ plans sold whose Churn Date (last plan + 180 days) falls in the period',
    query:
      'SELECT Churn_Date__c FROM Encore_Firm__c WHERE EEP_Firm_ID__c != null AND Total_Plans_Sold__c >= 7 AND Churn_Date__c >= {startDate} AND Churn_Date__c <= TODAY',
    dateField: 'Churn_Date__c',
    aggregation: 'count',
    format: 'number',
    invertedPolarity: true,
    isPrimary: true,
    segmentObject: 'Encore_Firm__c',
  },

  // ── ACCOUNT MILESTONES ─────────────────────────────────────
  {
    key: 'seventh-plans-completed',
    name: 'Seventh Plans Completed',
    section: 'ACCOUNT MILESTONES',
    description: 'Firms that completed their 7th plan in the period (activation milestone)',
    query:
      'SELECT Seventh_Plan_Date__c FROM Encore_Firm__c WHERE EEP_Firm_ID__c != null AND Seventh_Plan_Date__c >= {startDate}',
    dateField: 'Seventh_Plan_Date__c',
    aggregation: 'count',
    format: 'number',
    isPrimary: true,
    segmentObject: 'Encore_Firm__c',
  },
  {
    key: 'time-to-first-plan',
    name: 'Avg Days to First Plan',
    section: 'ACCOUNT MILESTONES',
    description: 'Average days from firm creation to first plan, for firms reaching their first plan in the period',
    query: '',
    dateField: 'First_Plan_Date__c',
    customFetcher: fetchTimeToFirstPlan,
    aggregation: 'avg',
    valueField: 'elapsedDays',
    format: 'decimal',
    invertedPolarity: true, // fewer days to activate is better
  },
  {
    key: 'time-to-seventh-plan',
    name: 'Avg Days to 7th Plan',
    section: 'ACCOUNT MILESTONES',
    description: 'Average days from firm creation to 7th plan, for firms reaching their 7th plan in the period',
    query: '',
    dateField: 'Seventh_Plan_Date__c',
    customFetcher: fetchTimeToSeventhPlan,
    aggregation: 'avg',
    valueField: 'elapsedDays',
    format: 'decimal',
    invertedPolarity: true,
  },

  // ── USERS ─────────────────────────────────────────────────
  {
    key: 'new-users',
    name: 'New Users',
    section: 'NEW USERS',
    description: 'Contacts with an EEP User ID created in the period',
    query:
      'SELECT User_Created_Date__c FROM Contact WHERE EEP_User_ID__c != null AND User_Created_Date__c >= {startDate}',
    dateField: 'User_Created_Date__c',
    aggregation: 'count',
    format: 'number',
    isPrimary: true,
    segmentObject: 'Contact',
  },

  // ── REVENUE ───────────────────────────────────────────────
  {
    key: 'package-revenue',
    name: 'Package Revenue',
    section: 'PACKAGE REVENUE',
    description: 'Revenue from won plan package opportunities (one-time plan purchases)',
    query:
      "SELECT CloseDate, Amount FROM Opportunity WHERE IsWon = true AND Order_Type__c = 'PLAN_PACKAGE' AND RecordTypeId = '012Vs000004gYUQIA2' AND Amount != null AND CloseDate >= {startDate}",
    dateField: 'CloseDate',
    aggregation: 'sum',
    valueField: 'Amount',
    format: 'currency',
    isPrimary: true,
    segmentObject: 'Opportunity',
  },
  {
    key: 'subscription-revenue',
    name: 'Subscription Revenue',
    section: 'SUBSCRIPTION REVENUE',
    description: 'Revenue from won subscription opportunities (recurring revenue)',
    query:
      "SELECT CloseDate, Amount FROM Opportunity WHERE IsWon = true AND Order_Type__c = 'SUBSCRIPTION' AND Amount != null AND CloseDate >= {startDate}",
    dateField: 'CloseDate',
    aggregation: 'sum',
    valueField: 'Amount',
    format: 'currency',
    isPrimary: true,
    segmentObject: 'Opportunity',
  },

  // ── PLANS COMPLETED ────────────────────────────────────────
  {
    key: 'plans-completed-trusts',
    name: 'Trusts',
    section: 'PLANS COMPLETED',
    description: 'Trust-based plans submitted (non-amendments)',
    query:
      "SELECT Submitted_Date__c FROM Opportunity WHERE IsWon = true AND RecordTypeId = '012Vs000004gYUQIA2' AND Order_Type__c = 'PLAN' AND Plan_Type__c = 'TRUST_BASED_PLAN' AND Is_Plan_Amendment__c = false AND Submitted_Date__c >= {startDateTime}",
    dateField: 'Submitted_Date__c',
    aggregation: 'count',
    format: 'number',
    segmentObject: 'Opportunity',
    includeDeactivated: true,
  },
  {
    key: 'plans-completed-wills',
    name: 'Wills',
    section: 'PLANS COMPLETED',
    description: 'Will-based plans submitted (non-amendments)',
    query:
      "SELECT Submitted_Date__c FROM Opportunity WHERE IsWon = true AND RecordTypeId = '012Vs000004gYUQIA2' AND Order_Type__c = 'PLAN' AND Plan_Type__c = 'WILL_BASED_PLAN' AND Is_Plan_Amendment__c = false AND Submitted_Date__c >= {startDateTime}",
    dateField: 'Submitted_Date__c',
    aggregation: 'count',
    format: 'number',
    segmentObject: 'Opportunity',
    includeDeactivated: true,
  },
  {
    key: 'plans-completed-summaries',
    name: 'Summaries',
    section: 'PLANS COMPLETED',
    description: 'Plan summaries submitted (non-amendments)',
    query:
      "SELECT Submitted_Date__c FROM Opportunity WHERE IsWon = true AND RecordTypeId = '012Vs000004gYUQIA2' AND Order_Type__c = 'PLAN' AND Plan_Type__c = 'PLAN_SUMMARY' AND Is_Plan_Amendment__c = false AND Submitted_Date__c >= {startDateTime}",
    dateField: 'Submitted_Date__c',
    aggregation: 'count',
    format: 'number',
    segmentObject: 'Opportunity',
    includeDeactivated: true,
  },
  {
    key: 'plans-completed-powers',
    name: 'Powers',
    section: 'PLANS COMPLETED',
    description: 'Powers plans submitted (non-amendments)',
    query:
      "SELECT Submitted_Date__c FROM Opportunity WHERE IsWon = true AND RecordTypeId = '012Vs000004gYUQIA2' AND Order_Type__c = 'PLAN' AND Plan_Type__c = 'POWERS_PLAN' AND Is_Plan_Amendment__c = false AND Submitted_Date__c >= {startDateTime}",
    dateField: 'Submitted_Date__c',
    aggregation: 'count',
    format: 'number',
    segmentObject: 'Opportunity',
    includeDeactivated: true,
  },
  {
    key: 'plans-completed-amendments',
    name: 'Amendments',
    section: 'PLANS COMPLETED',
    description: 'Amendments to existing plans submitted',
    query:
      "SELECT Submitted_Date__c FROM Opportunity WHERE IsWon = true AND RecordTypeId = '012Vs000004gYUQIA2' AND Order_Type__c = 'PLAN' AND Is_Plan_Amendment__c = true AND Submitted_Date__c >= {startDateTime}",
    dateField: 'Submitted_Date__c',
    aggregation: 'count',
    format: 'number',
    segmentObject: 'Opportunity',
    includeDeactivated: true,
  },
  {
    key: 'plans-completed-total',
    name: 'Total',
    section: 'PLANS COMPLETED',
    description: 'All plans submitted (all types including amendments)',
    query:
      "SELECT Submitted_Date__c FROM Opportunity WHERE IsWon = true AND RecordTypeId = '012Vs000004gYUQIA2' AND Order_Type__c = 'PLAN' AND Submitted_Date__c >= {startDateTime}",
    dateField: 'Submitted_Date__c',
    aggregation: 'count',
    format: 'number',
    isPrimary: true,
    segmentObject: 'Opportunity',
    includeDeactivated: true,
  },

  // ── PLANS STARTED ─────────────────────────────────────────
  {
    key: 'plans-started-trusts',
    name: 'Trusts',
    section: 'PLANS STARTED',
    description: 'Trust-based plans created (non-amendments)',
    query:
      "SELECT Plan_Created_Date__c FROM Opportunity WHERE RecordTypeId = '012Vs000004gYUQIA2' AND Plan_Type__c = 'TRUST_BASED_PLAN' AND Is_Plan_Amendment__c = false AND Plan_Created_Date__c >= {startDate}",
    dateField: 'Plan_Created_Date__c',
    aggregation: 'count',
    format: 'number',
    segmentObject: 'Opportunity',
  },
  {
    key: 'plans-started-wills',
    name: 'Wills',
    section: 'PLANS STARTED',
    description: 'Will-based plans created (non-amendments)',
    query:
      "SELECT Plan_Created_Date__c FROM Opportunity WHERE RecordTypeId = '012Vs000004gYUQIA2' AND Plan_Type__c = 'WILL_BASED_PLAN' AND Is_Plan_Amendment__c = false AND Plan_Created_Date__c >= {startDate}",
    dateField: 'Plan_Created_Date__c',
    aggregation: 'count',
    format: 'number',
    segmentObject: 'Opportunity',
  },
  {
    key: 'plans-started-summaries',
    name: 'Summaries',
    section: 'PLANS STARTED',
    description: 'Plan summaries created (non-amendments)',
    query:
      "SELECT Plan_Created_Date__c FROM Opportunity WHERE RecordTypeId = '012Vs000004gYUQIA2' AND Plan_Type__c = 'PLAN_SUMMARY' AND Is_Plan_Amendment__c = false AND Plan_Created_Date__c >= {startDate}",
    dateField: 'Plan_Created_Date__c',
    aggregation: 'count',
    format: 'number',
    segmentObject: 'Opportunity',
  },
  {
    key: 'plans-started-powers',
    name: 'Powers',
    section: 'PLANS STARTED',
    description: 'Powers plans created (non-amendments)',
    query:
      "SELECT Plan_Created_Date__c FROM Opportunity WHERE RecordTypeId = '012Vs000004gYUQIA2' AND Plan_Type__c = 'POWERS_PLAN' AND Is_Plan_Amendment__c = false AND Plan_Created_Date__c >= {startDate}",
    dateField: 'Plan_Created_Date__c',
    aggregation: 'count',
    format: 'number',
    segmentObject: 'Opportunity',
  },
  {
    key: 'plans-started-amendments',
    name: 'Amendments',
    section: 'PLANS STARTED',
    description: 'Amendments to existing plans created',
    query:
      "SELECT Plan_Created_Date__c FROM Opportunity WHERE RecordTypeId = '012Vs000004gYUQIA2' AND Is_Plan_Amendment__c = true AND Plan_Created_Date__c >= {startDate}",
    dateField: 'Plan_Created_Date__c',
    aggregation: 'count',
    format: 'number',
    segmentObject: 'Opportunity',
  },
  {
    key: 'plans-started-total',
    name: 'Total',
    section: 'PLANS STARTED',
    description: 'All plans created (all types including amendments)',
    query:
      "SELECT Plan_Created_Date__c FROM Opportunity WHERE RecordTypeId = '012Vs000004gYUQIA2' AND Plan_Created_Date__c >= {startDate}",
    dateField: 'Plan_Created_Date__c',
    aggregation: 'count',
    format: 'number',
    isPrimary: true,
    segmentObject: 'Opportunity',
  },

  // ── DEED FILINGS ────────────────────────────────────────────
  {
    key: 'deed-filings',
    name: 'Deed Filings',
    section: 'DEED FILINGS',
    description: 'Weekly deed filing totals, sourced from a public Google Sheet (not Salesforce)',
    query: '',
    dateField: 'weekEnding',
    customFetcher: fetchDeedFilings,
    aggregation: 'sum',
    valueField: 'filings',
    format: 'number',
    isPrimary: true,
  },
];
